import React, { useEffect, useState } from "react";
import { apiClient } from "../lib/api";
import { useT } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { Award, Plus, Trash2, Megaphone, Image as ImageIcon, Users } from "lucide-react";

function GrandPianoIcon({ className }) {
  return (
    <svg viewBox="0 0 140 80" className={className} aria-hidden>
      <path d="M10 60 Q 70 12 130 60 L 130 70 L 10 70 Z" fill="#0a0a0a" />
      <rect x="18" y="62" width="104" height="8" fill="#fff" stroke="#0a0a0a" strokeWidth="0.8" />
      <rect x="18" y="62" width="104" height="2" fill="#ffb703" opacity="0.9" />
    </svg>
  );
}

const EMPTY_FORM = {
  title: "", message: "", date: "", time: "18:00", duration_minutes: 60, location: "",
  venue_photos: [],
  sponsors: [],
  student_ids: [],
};

export default function RecitalsPage() {
  const t = useT();
  const [recitals, setRecitals] = useState([]);
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectionMode, setSelectionMode] = useState("toggled"); // toggled | manual
  const [manualSelection, setManualSelection] = useState([]);
  const [broadcastFor, setBroadcastFor] = useState(null); // recital object currently being messaged
  const [broadcastText, setBroadcastText] = useState("");

  const load = async () => {
    const [rR, sR] = await Promise.all([apiClient.get("/recitals"), apiClient.get("/students")]);
    setRecitals(rR.data.recitals); setStudents(sR.data.students);
  };
  useEffect(() => { load(); }, []);

  const readImg = (file, cb) => { const r = new FileReader(); r.onload = () => cb(r.result); r.readAsDataURL(file); };

  const addVenuePhoto = (file) => { readImg(file, (d) => setForm((f) => ({ ...f, venue_photos: [...f.venue_photos, d] }))); };
  const removeVenuePhoto = (idx) => setForm((f) => ({ ...f, venue_photos: f.venue_photos.filter((_, i) => i !== idx) }));

  const addSponsor = () => setForm((f) => ({ ...f, sponsors: [...f.sponsors, { name: "", photo_data_url: null, url: "" }] }));
  const updateSponsor = (idx, patch) => setForm((f) => ({ ...f, sponsors: f.sponsors.map((s, i) => i === idx ? { ...s, ...patch } : s) }));
  const removeSponsor = (idx) => setForm((f) => ({ ...f, sponsors: f.sponsors.filter((_, i) => i !== idx) }));

  const addStudentsToForm = () => {
    if (selectionMode === "toggled") {
      const toggled = students.filter((s) => s.will_attend_recital).map((s) => s.id);
      setForm((f) => ({ ...f, student_ids: Array.from(new Set([...f.student_ids, ...toggled])) }));
      toast.success(`${t("Added")} ${toggled.length} ${t("toggled students")}`);
    } else {
      setForm((f) => ({ ...f, student_ids: Array.from(new Set([...f.student_ids, ...manualSelection])) }));
      toast.success(`${t("Added")} ${manualSelection.length} ${t("students")}`);
      setManualSelection([]);
    }
  };

  const toggleManual = (id) => {
    setManualSelection((prev) => prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]);
  };

  const save = async () => {
    if (!form.title || !form.date) return toast.error(t("Title and date required"));
    const payload = { ...form };
    await apiClient.post("/recitals", payload);
    setOpen(false); setForm(EMPTY_FORM); load(); toast.success(t("Recital scheduled"));
  };

  const del = async (id) => { if (!window.confirm(t("Delete recital?"))) return; await apiClient.delete(`/recitals/${id}`); load(); };

  const sendBroadcast = async () => {
    if (!broadcastText.trim()) return toast.error(t("Write a message first"));
    await apiClient.post(`/recitals/${broadcastFor.id}/broadcast`, { text: broadcastText.trim() });
    toast.success(`${t("Message sent to")} ${(broadcastFor.student_ids || []).length} ${t("attendees")}`);
    setBroadcastText(""); setBroadcastFor(null); load();
  };

  return (
    <div className="space-y-6" data-testid="recitals-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <GrandPianoIcon className="w-20 h-12" />
          <div>
            <h1 className="font-display text-4xl font-bold flex items-center gap-3"><Award className="w-7 h-7" /> {t("Recitals")}</h1>
            <p style={{ color: "var(--app-text-muted)" }}>{t("Plan performances, invite students, share venue photos, recognise sponsors, message your attendees.")}</p>
          </div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="btn-pill btn-primary" data-testid="plan-recital-btn"><Plus className="w-4 h-4" /> {t("Plan next recital")}</Button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{t("Plan a recital")}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>{t("Title *")}</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="recital-title" /></div>
              <div><Label>{t("Message (optional)")}</Label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="recital-message" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>{t("Date *")}</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="recital-date" /></div>
                <div><Label>{t("Time")}</Label><Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} /></div>
                <div><Label>{t("Duration (min)")}</Label><Input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: Number(e.target.value) })} /></div>
              </div>
              <div><Label>{t("Location")}</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>

              {/* Venue photos (multiple) */}
              <div className="bento-card p-4" data-testid="venue-photos-section">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-display font-bold flex items-center gap-2"><ImageIcon className="w-4 h-4" /> {t("Venue photos")} ({form.venue_photos.length})</div>
                  <label className="btn-pill btn-outline text-xs cursor-pointer">
                    <Plus className="w-3 h-3" /> {t("Add photo")}
                    <input type="file" accept="image/*" multiple className="hidden"
                           onChange={(e) => {
                             const files = Array.from(e.target.files || []);
                             files.forEach(addVenuePhoto);
                             e.target.value = "";
                           }}
                           data-testid="venue-photo-input" />
                  </label>
                </div>
                {form.venue_photos.length === 0 ? (
                  <p className="text-xs" style={{ color: "var(--app-text-muted)" }}>{t("No photos yet — show off the hall!")}</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {form.venue_photos.map((p, i) => (
                      <div key={i} className="relative" data-testid={`venue-photo-${i}`}>
                        <img alt={`venue ${i+1}`} src={p} className="w-full h-20 object-cover rounded-xl" />
                        <button type="button" onClick={() => removeVenuePhoto(i)}
                                className="absolute -top-2 -right-2 rounded-full bg-white border p-1 shadow"
                                aria-label={`${t("Remove venue photo")} ${i+1}`}
                                data-testid={`remove-venue-photo-${i}`}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Sponsors (multiple) */}
              <div className="bento-card p-4" data-testid="sponsors-section">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-display font-bold">{t("Sponsors")} ({form.sponsors.length})</div>
                  <Button type="button" variant="outline" onClick={addSponsor} className="text-xs h-8" data-testid="add-sponsor">
                    <Plus className="w-3 h-3" /> {t("Add sponsor")}
                  </Button>
                </div>
                {form.sponsors.length === 0 ? (
                  <p className="text-xs" style={{ color: "var(--app-text-muted)" }}>{t("Thank the local brands supporting the event.")}</p>
                ) : (
                  <div className="space-y-3">
                    {form.sponsors.map((s, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 items-start" data-testid={`sponsor-${i}`}>
                        <div className="col-span-4">
                          <Input placeholder={t("Sponsor name")} value={s.name || ""}
                                 onChange={(e) => updateSponsor(i, { name: e.target.value })}
                                 data-testid={`sponsor-name-${i}`} />
                        </div>
                        <div className="col-span-4">
                          <Input placeholder="https://sponsor-site.com" value={s.url || ""}
                                 onChange={(e) => updateSponsor(i, { url: e.target.value })}
                                 data-testid={`sponsor-url-${i}`} />
                        </div>
                        <div className="col-span-3">
                          {s.photo_data_url ? (
                            <img alt="sponsor" src={s.photo_data_url} className="w-full h-10 object-cover rounded-md" />
                          ) : (
                            <label className="btn-pill btn-outline text-xs cursor-pointer w-full justify-center">
                              {t("Logo")}
                              <input type="file" accept="image/*" className="hidden"
                                     onChange={(e) => e.target.files?.[0] && readImg(e.target.files[0], (d) => updateSponsor(i, { photo_data_url: d }))}
                                     data-testid={`sponsor-logo-input-${i}`} />
                            </label>
                          )}
                        </div>
                        <div className="col-span-1 flex justify-end">
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeSponsor(i)}
                                  data-testid={`remove-sponsor-${i}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Attendees */}
              <div className="bento-card p-4">
                <div className="font-display font-bold mb-2 flex items-center gap-2"><Users className="w-4 h-4" /> {t("Attendees")} ({form.student_ids.length})</div>
                <div className="flex gap-2 mb-2">
                  <Button variant={selectionMode === "toggled" ? "default" : "outline"} onClick={() => setSelectionMode("toggled")} data-testid="mode-toggled">{t("Use toggled students")}</Button>
                  <Button variant={selectionMode === "manual" ? "default" : "outline"} onClick={() => setSelectionMode("manual")} data-testid="mode-manual">{t("Pick manually")}</Button>
                </div>
                {selectionMode === "manual" && (
                  <>
                    <p className="text-xs mb-2" style={{ color: "var(--app-text-muted)" }}>{t("Highlight the students attending recital and then hit \"Add students\" button.")}</p>
                    <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                      {students.map((s) => {
                        const sel = manualSelection.includes(s.id);
                        return (
                          <button key={s.id} type="button" onClick={() => toggleManual(s.id)}
                                  data-testid={`manual-student-${s.id}`}
                                  className={`px-3 py-1.5 rounded-full text-sm font-semibold border-2 ${sel ? "text-white" : ""}`}
                                  style={{ background: sel ? "#1e90ff" : "var(--app-surface)", borderColor: sel ? "#1e90ff" : "var(--app-border)" }}>
                            {s.name}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
                <Button onClick={addStudentsToForm} className="btn-pill btn-primary mt-3 h-9" data-testid="add-students-to-recital">
                  {selectionMode === "toggled" ? t("Add all toggled") : t("Add selected")}
                </Button>
              </div>
              <Button onClick={save} className="btn-pill btn-primary h-11 w-full justify-center" data-testid="save-recital">{t("Save recital")}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {recitals.length === 0 ? (
        <div className="bento-card p-10 text-center">
          <GrandPianoIcon className="w-40 h-24 mx-auto" />
          <p className="mt-3" style={{ color: "var(--app-text-muted)" }}>{t("No recitals scheduled. Plan one to get started.")}</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {recitals.map((r) => {
            const venuePhotos = (r.venue_photos && r.venue_photos.length ? r.venue_photos : (r.venue_photo_data_url ? [r.venue_photo_data_url] : []));
            const sponsors = (r.sponsors && r.sponsors.length ? r.sponsors : (r.sponsor_photo_data_url ? [{ name: "Sponsor", photo_data_url: r.sponsor_photo_data_url, url: "" }] : []));
            const broadcasts = r.broadcasts || [];
            return (
              <div key={r.id} className="bento-card p-5 flex flex-col gap-3" data-testid={`recital-${r.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-display text-xl font-bold">🎤 {r.title}</div>
                    <div className="text-sm" style={{ color: "var(--app-text-muted)" }}>{r.date} {t("at")} {r.time} · {r.duration_minutes} {t("min")}</div>
                    {r.location && <div className="text-sm mt-1">{r.location}</div>}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" onClick={() => { setBroadcastFor(r); setBroadcastText(""); }}
                            data-testid={`broadcast-btn-${r.id}`}>
                      <Megaphone className="w-4 h-4" /> {t("Message")}
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => del(r.id)} data-testid={`delete-recital-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
                {r.message && <p className="text-sm">{r.message}</p>}
                {venuePhotos.length > 0 && (
                  <div className="grid grid-cols-3 gap-2" data-testid={`recital-${r.id}-venue-photos`}>
                    {venuePhotos.map((p, i) => (
                      <img key={i} alt={`venue ${i+1}`} src={p} className="w-full h-24 object-cover rounded-xl" />
                    ))}
                  </div>
                )}
                {sponsors.length > 0 && (
                  <div data-testid={`recital-${r.id}-sponsors`}>
                    <div className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: "var(--app-text-muted)" }}>
                      {t("Sponsored by")}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {sponsors.map((s, i) => (
                        <a key={i} href={s.url || "#"} target={s.url ? "_blank" : undefined} rel="noreferrer"
                           className="inline-flex items-center gap-2 px-2 py-1 rounded-full border text-xs"
                           style={{ borderColor: "var(--app-border)", background: "var(--app-surface-2)" }}>
                          {s.photo_data_url && <img alt="" src={s.photo_data_url} className="w-5 h-5 rounded-full object-cover" />}
                          <span className="font-semibold">{s.name || t("Sponsor")}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                {broadcasts.length > 0 && (
                  <div className="rounded-xl p-3" style={{ background: "var(--app-surface-2)" }} data-testid={`recital-${r.id}-broadcasts`}>
                    <div className="text-xs uppercase tracking-wider font-bold mb-1" style={{ color: "var(--app-text-muted)" }}>
                      {t("Messages to attendees")}
                    </div>
                    <ul className="space-y-1">
                      {broadcasts.slice().reverse().map((b) => (
                        <li key={b.id || b.at} className="text-sm">
                          <span style={{ color: "var(--app-text-muted)" }}>{b.at?.slice(0, 10)} — </span>{b.text}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="text-xs"><b>{(r.student_ids || []).length}</b> {t("attendees")}</div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!broadcastFor} onOpenChange={(v) => { if (!v) setBroadcastFor(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Megaphone className="w-5 h-5" /> {t("Message attendees")}</DialogTitle>
          </DialogHeader>
          {broadcastFor && (
            <div className="space-y-3">
              <div className="text-sm" style={{ color: "var(--app-text-muted)" }}>
                {t("Sending to")} <b style={{ color: "var(--app-text)" }}>{(broadcastFor.student_ids || []).length}</b> {t("attendees")} {t("of")} <b style={{ color: "var(--app-text)" }}>{broadcastFor.title}</b>.
              </div>
              <Textarea
                rows={5}
                value={broadcastText}
                onChange={(e) => setBroadcastText(e.target.value)}
                placeholder={t("Dress code is formal. Please arrive by 5:30 for warm-ups!")}
                data-testid="broadcast-textarea"
              />
              <Button onClick={sendBroadcast} className="btn-pill btn-primary h-10 w-full justify-center" data-testid="broadcast-send">
                {t("Send message")}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
